<template>
	<List
		:image="article.image"
		:image-text="article.title"
		:flags="article.flags"
	>
		<template slot="checkbox">
			<transition name="fade-transition">
				<slot name="checkbox" />
			</transition>
		</template>
		<template slot="header">
			<div class="body-1 my-0">
				<span class="body-2 secondary--text">{{ article.userName }}</span>
				added
				<span class="secondary--text">{{ article.timeAddedAgo }}</span>
			</div>
			<v-spacer />
			<ArticleImportance
				v-if="!isArchive"
				:article="article"
			/>
		</template>
		<template slot="title">
			<div class="sub-title">
				<span
					v-if="article.author"
					:title="article.author"
					class="truncate"
				>
					{{ article.author }}
				</span>
				<span v-if="article.author && article.date"> / </span>
				{{ article.date }}
				<span v-if="article.date || (article.author && !article.date)">
					/
				</span>
				<v-tooltip v-if="article.publication" right>
					<span slot="activator">{{ article.publicationName }}</span>
					<div>
						<div class="body-2">{{ article.publicationName }}</div>
						<div>
							Reach:
							{{ article.publication.alexaStats.reachUsers | shortNumber }}
						</div>
					</div>
				</v-tooltip>
				<span v-else>{{ article.publicationName }}</span>
				<span
					v-if="(article.author || article.date || article.publicationName) && category"
				> / </span>
				<span title="category">{{ category }}</span>
			</div>
			<div class="body-2">
				<span :title="article.title" class="truncate">
					{{ article.title }}
				</span>
			</div>
			<div class="sub-title">
				<a :href="article.url" target="_blank">
					{{ article.url }}
				</a>
			</div>
		</template>
		<template slot="additional">
			<v-flex hidden-sm-and-down md1 class="social-chips">
				<v-chip label class="v-chip__facebook" text-color="white">
					<v-icon left>fa fa-facebook</v-icon>{{ article.facebookShares | formatNumber }}
				</v-chip>
				<v-chip
					v-if="this.$store.get('isSkilled', 'twitter')"
					label class="v-chip__twitter"
					text-color="white"
				>
					<v-icon left>fa fa-twitter</v-icon>{{ article.twitterShares }}
				</v-chip>
			</v-flex>
		</template>
		<template slot="actions">
			<v-flex class="pl-0">
				<v-card-actions>
					<v-spacer />
					<v-tooltip v-if="article.errorsSum > 0" top>
						<v-btn slot="activator" flat color="info" icon>
							<v-icon>info</v-icon>
						</v-btn>
						<div
							v-for="(error, index) in article.errors"
							:key="index"
						>
							{{ error }}
						</div>
					</v-tooltip>
					<v-btn
						v-if="!$vuetify.breakpoint.smAndDown && !isArchive && $userCan('manage_reports') && !report.isArchivedOrParentArchived"
						:disabled="article.changing"
						slot="activator"
						ripple
						flat
						@click.native.stop="$emit('edit', article)"
					>
						Edit
					</v-btn>
					<v-btn
						v-if="isArchive && $userCan('manage_reports') && !report.isArchivedOrParentArchived"
						:disabled="article.changing"
						slot="activator"
						ripple
						flat
						color="success"
						@click="$emit('restore', article)"
					>
						Restore
					</v-btn>
					<v-menu
						v-if="!isArchive"
						v-model="additionalMenu"
						bottom
						left
					>
						<v-btn
							v-if="$userCan('manage_reports') && !report.isArchivedOrParentArchived"
							slot="activator"
							:disabled="article.changing"
							small
							icon
						>
							<v-icon>more_vert</v-icon>
						</v-btn>
						<v-list v-if="additionalMenu">
							<v-list-tile
								v-if="$vuetify.breakpoint.smAndDown"
								@click="$emit('edit', article)"
							>
								<v-list-tile-title>Edit Article</v-list-tile-title>
							</v-list-tile>
							<v-list-tile
								@click="article.updateSocialStats()"
							>
								<v-list-tile-title>Update Social Stats</v-list-tile-title>
							</v-list-tile>
							<v-list-tile @click="$emit('set-image', article)">
								<v-list-tile-title>Set Image</v-list-tile-title>
							</v-list-tile>
							<v-list-tile
								v-if="!hideImageSettings"
								@click="$emit('set-report-image', article.image)"
							>
								<v-list-tile-title>Set as Report Image</v-list-tile-title>
							</v-list-tile>
							<v-list-tile
								v-if="$userCan('manage_projects') && !hideImageSettings"
								@click="$emit('set-project-image', article.image)"
							>
								<v-list-tile-title>Set as Project Image</v-list-tile-title>
							</v-list-tile>
							<v-list-tile @click="$store.set('articlesBulkDialog/openArticleBulkDialog!', { action: 'move', articles: [article] })">
								<v-list-tile-title>Move Article</v-list-tile-title>
							</v-list-tile>
							<v-list-tile @click="$store.set('articlesBulkDialog/openArticleBulkDialog!', { action: 'copy', articles: [article] })">
								<v-list-tile-title>Copy Article</v-list-tile-title>
							</v-list-tile>
							<v-list-tile @click="$store.set('articlesDeleteDialog/openArticleDeleteDialog!', [article])">
								<v-list-tile-title class="error--text">Delete Article</v-list-tile-title>
							</v-list-tile>
						</v-list>
					</v-menu>
				</v-card-actions>
			</v-flex>
		</template>
	</List>
</template>

<script>
import List from '@/components/List';
import ArticleImportance from '@/components/Article/ArticleImportance';

export default {
	components: {
		List,
		ArticleImportance,
	},
	props: {
		article: { type: Object, required: true },
		hideImageSettings: { type: Boolean, default: false },
		isArchive: { type: Boolean, default: false },
	},
	data() {
		return {
			pubLogoBaseURL: 'https://logo.clearbit.com/',
			additionalMenu: false,
		};
	},
	computed: {
		report() {
			return this.$store.get('reports/currentReport');
		},
		categories() {
			return this.$store.get('categories');
		},
		category() {
			const id = this.article.categoryId;
			const category = this.categories.find(cat => cat.id === id);
			if (category) {
				return category.name;
			}
			return null;
		},
	},
};
</script>

<style lang="scss" scoped>
.small-btn {
	margin: 0;
	height: 12px;
	i {
		font-size: 20px;
	}
}
.sub-title,
.body-2 {
	max-width: 100%;
	.truncate {
		max-width: 100%;
		overflow: hidden;
		text-overflow: ellipsis;
		vertical-align: bottom;
		display: inline-block;
	}
	a {
		text-overflow: ellipsis;
		overflow: hidden;
		white-space: nowrap;
	}
}
.v-chip__twitter {
	background-color: #1da1f2;
}
.v-chip__facebook {
	background-color: #3b5998;
}
.social-chips {
	display: flex;
	flex-direction: column;
	align-items: center;
}
</style>
